import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import PublicRouteWrapper from "../PublicRouteWrapper";
import MainContent from "../MainContent";
import { routes } from "../../shared";
import Loader from "../../components/Loader";

const Signin = lazy(() => import("../../pages/Signin"));
const MagicSignin = lazy(() => import("../../components/CreateAccount"));
const CreatePassword = lazy(() => import("../../components/CreatePassword"));
const ReviewInfo = lazy(() => import("../../components/ReviewInfo"));
const Dashboard = lazy(() => import("../../pages/Dashboard"));
const CheckEmailSent = lazy(() => import("../../components/CheckEmailSent"));
const PendingProfile = lazy(() => import("../../components/PendingProfile"));
const CheckUser = lazy(() => import("../../pages/Signin/CheckUser"));
const CreateAccount = lazy(() => import("../../components/CreateAccount"));
const WelcomeBack = lazy(() => import("../../pages/Signin/WelcomeBack"));
const SigninWithPassword = lazy(
  () => import("../../pages/Signin/SigninWithPassword")
);
const FeedbackSubmission = lazy(() => import("../../pages/FeedbackSubmission"));
const Feedbacks = lazy(() => import("../../pages/Feedbacks"));
const FeedbacksToProvide = lazy(
  () => import("../../pages/Feedbacks/FeedbacksToProvide")
);
const MyFeedbacks = lazy(() => import("../../pages/Feedbacks/MyFeedbacks"));
const EmployeeFeedbackView = lazy(
  () => import("../../pages/Feedbacks/EmployeeView")
);
const EmployeeFeedbackOverview = lazy(
  () => import("../../components/EmployeeFeedbackOverview")
);
const EmployeeFeedbackDetail = lazy(
  () => import("../../components/EmployeeFeedbackDetail")
);
const AssessmentCreation = lazy(
  () => import("../../pages/Assessments/AssessmentCreation")
);
const AssessmentConfiguration = lazy(
  () => import("../../pages/Assessments/AssessmentConfiguration")
);
const AssessmentList = lazy(
  () => import("../../pages/Assessments/AssessmentList")
);
const ProfileCompletion = lazy(() => import("../../pages/ProfileCompletion"));
const AssessmentMonitoring = lazy(
  () => import("../../pages/Assessments/AssessmentMonitoring")
);
const Team = lazy(() => import("../../pages/Team"));
const Landing = lazy(() => import("../../pages/Landing"));
const Assessments = lazy(() => import("../../pages/Assessments"));
const People = lazy(() => import("../../pages/People"));
const PeopleList = lazy(() => import("../../pages/People/PeopleList"));
const EmployeeMonitoring = lazy(
  () => import("../../pages/People/EmployeeMonitoring")
);
const EmployeeOverview = lazy(() => import("../../components/EmployeeOverview"));
const EmployeeDetailedResults = lazy(
  () => import("../../components/EmployeeDetailedResults")
);
const TermsOfService = lazy(() => import("../../components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("../../components/PrivacyPolicy"));
const Account = lazy(() => import("../../pages/Account"));

const RouteLoader = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Loader isContainerWide />
  </div>
);

const RoutesWrapper = () => {
  const location = useLocation();

  const intl = useIntl();

  const titles = useMemo(() => {
    return {
      "/": `${intl.formatMessage({ id: "general.landingTitle" })} | Circlefeed`,
      [routes.DASHBOARD]: `${intl.formatMessage({
        id: "general.dashboard",
      })} | Circlefeed`,
      [routes.ASSESSMENTS]: `${intl.formatMessage({
        id: "general.assessmentsList",
      })} | Circlefeed`,
      [routes.FEEDBACKS]: `${intl.formatMessage({
        id: "general.myFeedbacks",
      })} | Circlefeed`,
      [routes.FEEDBACKS_TO_PROVIDE]: `${intl.formatMessage({
        id: "general.feedbacksToProvide",
      })} | Circlefeed`,
    };
  }, [intl]);

  useEffect(
    () =>
      // @ts-ignore
      (document.title = titles[location.pathname] ?? "CircleFeed"),
    [location, titles]
  );

  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Flow with People background image: Signin/Signup flow */}
        <Route path="/" element={<PublicRouteWrapper />}>
          <Route index element={<Landing />} />
          <Route path={routes.SIGNIN} element={<Signin />}>
            <Route index element={<CheckUser />} />
            <Route path={routes.WELCOME_BACK} element={<WelcomeBack />} />
            <Route
              path={routes.SIGNIN_WITH_PASSWORD}
              element={<SigninWithPassword />}
            />

            <Route path={routes.CREATE_ACCOUNT} element={<CreateAccount />} />
            <Route path={routes.CREATE_PASSWORD} element={<CreatePassword />} />
            <Route path={routes.CHECK_EMAIL} element={<CheckEmailSent />} />
            <Route path={routes.PENDING_PROFILE} element={<PendingProfile />} />
            <Route path={routes.SIGNIN_REVIEW_INFO} element={<ReviewInfo />} />
            <Route
              path={routes.SIGNIN_WITH_MAGIC_LINK}
              element={<MagicSignin />}
            />
            <Route
              path={routes.COMPLETE_PROFILE}
              element={<ProfileCompletion />}
            />
          </Route>
        </Route>
        {/* After signin/signup flow */}
        <Route path={routes.DASHBOARD} element={<Dashboard />}>
          <Route index element={<MainContent />} />
          <Route path={routes.ASSESSMENTS} element={<Assessments />}>
            <Route index element={<AssessmentList />} />
            <Route
              path={routes.CREATE_ASSESSMENT}
              element={<AssessmentCreation />}
            />
            <Route
              path={routes.CONFIGURE_ASSESSMENT}
              element={<AssessmentConfiguration />}
            />
            <Route
              path={routes.ASSESSMENT_MONITORING}
              element={<AssessmentMonitoring />}
            />
          </Route>
          <Route path={routes.TEAM} element={<Team />} />
          <Route path={routes.ACCOUNT} element={<Account />} />
          <Route path={routes.PEOPLE} element={<People />}>
            <Route index element={<PeopleList />} />
            <Route
              path={routes.EMPLOYEE_MONITOR}
              element={<EmployeeMonitoring />}
            >
              <Route index element={<EmployeeOverview />} />
              <Route
                path={routes.EMPLOYEE_DETAILED_RESULTS}
                element={<EmployeeDetailedResults />}
              />
            </Route>
          </Route>
          <Route path={routes.FEEDBACKS} element={<Feedbacks />}>
            <Route index element={<MyFeedbacks />} />
            <Route
              path={routes.FEEDBACKS_TO_PROVIDE}
              element={<FeedbacksToProvide />}
            />
            <Route
              path={routes.EMPLOYEE_FEEDBACK_MONITOR}
              element={<EmployeeFeedbackView />}
            >
              <Route index element={<EmployeeFeedbackOverview />} />
              <Route
                path={routes.EMPLOYEE_FEEDBACK_DETAILED_RESULT}
                element={<EmployeeFeedbackDetail />}
              />
            </Route>
          </Route>
        </Route>
        <Route path={routes.FEEDBACK} element={<FeedbackSubmission />} />
        <Route path={routes.TERMS_OF_SERVICE} element={<TermsOfService />} />
        <Route path={routes.PRIVACY} element={<PrivacyPolicy />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default RoutesWrapper;
